import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import {
  addCharge,
  correctCheckPayment,
  getChargeTypes,
  removeLoanCharge,
  unmatchLoanPayment,
  type ChargeTypeItem,
} from '../../endpoints/loans';
import type { ElfLoanDetailTransactionEntry } from '../../types/financeElfLoans';
import { getApiErrorMessage } from '../../utils/httpClientTs';
import ElfBorrowerPicker, { type ElfBorrowerChoice } from './ElfBorrowerPicker';

interface AddChargeDialogProps {
  open: boolean;
  loans: { id: string; loan_number: string }[];
  onClose: () => void;
  onDone: () => void;
}

export const AddChargeDialog: React.FC<AddChargeDialogProps> = ({
  open,
  loans,
  onClose,
  onDone,
}) => {
  const [loanId, setLoanId] = useState('');
  const [chargeTypes, setChargeTypes] = useState<ChargeTypeItem[]>([]);
  const [typeId, setTypeId] = useState('');
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [startDate, setStartDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loanKey = loans.map((loan) => loan.id).join(',');
  useEffect(() => {
    if (!open) return;
    setLoanId(loans.length === 1 ? loans[0].id : '');
    setTypeId('');
    setName('');
    setAmount('');
    setStartDate(new Date().toISOString().slice(0, 10));
    setError(null);
    setBusy(false);
    let cancel = false;
    getChargeTypes()
      .then((rows) => {
        if (!cancel) setChargeTypes(rows ?? []);
      })
      .catch((err) => {
        if (!cancel) setError(getApiErrorMessage(err));
      });
    return () => {
      cancel = true;
    };
    // loanKey captures the installment list without resetting the form on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loanKey]);

  const submit = async () => {
    const parsed = Number(amount);
    if (!loanId) {
      setError('Choose a loan.');
      return;
    }
    if (!name.trim()) {
      setError('Enter a charge name.');
      return;
    }
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError('Enter an amount greater than zero.');
      return;
    }
    if (!startDate) {
      setError('Enter a start date.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await addCharge(loanId, {
        name: name.trim(),
        amount: parsed,
        start_date: startDate,
      });
      onDone();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Add charge</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Posts a fee on one loan. If payments were already applied on or after
          this date, they are replayed so the fee is paid first.
        </DialogContentText>
        {loans.length > 1 && (
          <TextField
            select
            label="Loan"
            value={loanId}
            onChange={(e) => setLoanId(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          >
            {loans.map((loan) => (
              <MenuItem key={loan.id} value={loan.id}>
                {loan.loan_number || loan.id}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          select
          label="Charge type"
          value={typeId}
          onChange={(e) => {
            const id = e.target.value;
            setTypeId(id);
            const selected = chargeTypes.find((row) => row.id === id);
            if (selected) {
              setName(selected.name);
              setAmount(selected.amount.toFixed(2));
            }
          }}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
        >
          <MenuItem value="">
            <em>Custom</em>
          </MenuItem>
          {chargeTypes.map((row) => (
            <MenuItem key={row.id} value={row.id}>
              {row.name} — {row.amount.toFixed(2)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          size="small"
          disabled={busy}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Amount"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          fullWidth
          size="small"
          type="number"
          disabled={busy}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Start date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          fullWidth
          size="small"
          type="date"
          InputLabelProps={{ shrink: true }}
          disabled={busy}
        />
        {error && (
          <DialogContentText color="error" sx={{ mt: 2 }}>
            {error}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void submit()}
          disabled={busy}
        >
          {busy ? 'Adding…' : 'Add charge'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface UnmatchDialogProps {
  entry: ElfLoanDetailTransactionEntry | null;
  onClose: () => void;
  onDone: () => void;
}

export const RemoveChargeDialog: React.FC<UnmatchDialogProps> = ({
  entry,
  onClose,
  onDone,
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    setBusy(false);
  }, [entry]);

  const submit = async () => {
    if (!entry?.charge_id) return;
    setBusy(true);
    setError(null);
    try {
      await removeLoanCharge(entry.charge_id);
      onDone();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={!!entry}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Remove charge</DialogTitle>
      <DialogContent>
        <DialogContentText>
          This removes {entry?.charge_name || 'this charge'} from the loan.
          Later payments are replayed, so money that paid this fee moves to
          interest and principal.
        </DialogContentText>
        {error && (
          <DialogContentText color="error" sx={{ mt: 2 }}>
            {error}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void submit()}
          disabled={busy}
        >
          {busy ? 'Removing…' : 'Remove'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const UnmatchPayPalPaymentDialog: React.FC<UnmatchDialogProps> = ({
  entry,
  onClose,
  onDone,
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    setBusy(false);
  }, [entry]);

  const submit = async () => {
    if (!entry?.payment_id) return;
    setBusy(true);
    setError(null);
    try {
      await unmatchLoanPayment(entry.payment_id);
      onDone();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={!!entry}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Unmatch PayPal payment</DialogTitle>
      <DialogContent>
        <DialogContentText>
          This removes the payment from this borrower and returns the PayPal
          settlement to unmatched payments so it can be assigned to the correct
          person. It does not refund PayPal.
        </DialogContentText>
        {error && (
          <DialogContentText color="error" sx={{ mt: 2 }}>
            {error}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void submit()}
          disabled={busy}
        >
          {busy ? 'Unmatching…' : 'Unmatch'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface CorrectDialogProps {
  entry: ElfLoanDetailTransactionEntry | null;
  borrowerId: string;
  borrowerName: string;
  onClose: () => void;
  onDone: () => void;
}

export const CorrectCheckPaymentDialog: React.FC<CorrectDialogProps> = ({
  entry,
  borrowerId,
  borrowerName,
  onClose,
  onDone,
}) => {
  const [amount, setAmount] = useState('');
  const [postingDate, setPostingDate] = useState('');
  const [borrower, setBorrower] = useState<ElfBorrowerChoice | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const amountLocked = !!entry?.check_amount_locked;

  useEffect(() => {
    if (!entry) return;
    setAmount(Math.abs(entry.amount).toFixed(2));
    setPostingDate(entry.event_date || '');
    setBorrower(
      borrowerId ? { id: borrowerId, name: borrowerName || borrowerId } : null
    );
    setError(null);
    setBusy(false);
  }, [entry, borrowerId, borrowerName]);

  const submit = async () => {
    if (!entry?.payment_id || !borrower) return;
    const parsed = Number(amount);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError('Enter an amount greater than zero.');
      return;
    }
    if (!postingDate) {
      setError('Enter a posting date.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await correctCheckPayment(entry.payment_id, {
        amount: parsed,
        posting_date: postingDate,
        user_id: borrower.id,
      });
      onDone();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={!!entry}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Correct check payment</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Check {entry?.payment_ref ? `#${entry.payment_ref}` : ''}. Changing
          the borrower or date rewrites the loan books. The check amount stays
          put once the check is batched or deposited.
        </DialogContentText>
        <TextField
          label="Amount"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          fullWidth
          size="small"
          type="number"
          disabled={amountLocked || busy}
          helperText={
            amountLocked
              ? 'Amount is locked because this check is already batched or deposited.'
              : undefined
          }
          sx={{ mb: 2 }}
        />
        <TextField
          label="Posting date"
          value={postingDate}
          onChange={(e) => setPostingDate(e.target.value)}
          fullWidth
          size="small"
          type="date"
          InputLabelProps={{ shrink: true }}
          disabled={busy}
          sx={{ mb: 2 }}
        />
        <ElfBorrowerPicker selected={borrower} onSelect={setBorrower} />
        {error && (
          <DialogContentText color="error" sx={{ mt: 2 }}>
            {error}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void submit()}
          disabled={busy || !borrower}
        >
          {busy ? 'Saving…' : 'Save correction'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
